import { WorkflowObjectTypes } from '@interfaces/workflow';
import { generateClientNameString } from '@utils/generateClientNameString';

/**
 * `object` is a union of `User` and `ClientGroup` resolving to the completion's
 * subject, and is nullable: an account, goal or account group completion resolves
 * to `null` rather than to an object of another type.
 */
export type WorkflowCompletionRow = {
  id?: string;
  objectId?: string | null;
  objectType?: WorkflowObjectTypes | null;
  object?: Record<string, unknown> | null;
};

/**
 * Client name for a completion row, falling back to a label for its object type.
 * Client-scoped completions stay blank: some workflows (a calculator, for instance)
 * have no client at all, and repeating the column name would read as missing data.
 *
 * A household's `name` is read first because `generateClientNameString` falls back
 * to the bare id of an object it cannot otherwise name.
 */
export const workflowCompletionClientName = (
  { object, objectType }: WorkflowCompletionRow,
  t: (key: string) => string,
): string => {
  const householdName = object?.name;
  if (typeof householdName === 'string' && householdName) return householdName;

  const name = generateClientNameString(object ?? undefined);
  if (name) return name;
  if (!objectType || objectType === WorkflowObjectTypes.USER) return '';
  return t(`workflowCompletions:table.objectTypes.${objectType}`);
};

/**
 * Destination of a completion row, derived from `objectType` since `object` may be null.
 * Only client and household subjects have a page keyed by `objectId`; an account, goal
 * or account group lives under a nested route whose parent id this row does not carry,
 * so those rows are rendered without a link rather than pointing at a missing client.
 */
export const workflowCompletionHref = ({
  id,
  objectId,
  objectType,
}: WorkflowCompletionRow): string | undefined => {
  if (!objectId) return undefined;
  let page: string;
  if (objectType === WorkflowObjectTypes.HOUSEHOLD) {
    page = `/households/${objectId}`;
  } else if (!objectType || objectType === WorkflowObjectTypes.USER) {
    page = `/clients/${objectId}`;
  } else {
    return undefined;
  }
  return id ? `${page}?workflowCompletion=${id}` : page;
};
